import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Button,
  Input,
  Spacer,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";

import DefaultLayout from "@/layouts/default";
import { salesAPI, booksAPI } from "@/services/api";
import { useDirectionLanguage } from "@/components/DirectionLanguageContext";

// Sale item interface for individual book in a sale
interface SaleItem {
  book_id: string;
  book_name: string;
  quantity: number;
  price_per_book: number;
  subtotal: number;
}

// Sale interface matching the database structure
interface Sale {
  id: string;
  sale_date: string;
  buyer_name: string;
  buyer_phone: string;
  total_price: number;
  client_id: string;
  items: SaleItem[];
  created_at?: string;
  updated_at?: string;
}

interface Book {
  id: string;
  name: string;
  price_per_book: number;
  stock?: number;
}

const SaleEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [sale, setSale] = useState<Sale | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [books, setBooks] = useState<Book[]>([]);
  const [selectedBook, setSelectedBook] = useState<string | null>(null);
  const [selectedBookDisplay, setSelectedBookDisplay] = useState<string>("");
  const [newItemQuantity, setNewItemQuantity] = useState<number>(1);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRtl } = useDirectionLanguage();

  useEffect(() => {
    const fetchSale = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!id) {
          setError("Sale ID is missing");
          setLoading(false);

          return;
        }

        // Fetch books for dropdown
        const booksData = await booksAPI.getAll();

        setBooks(booksData);

        const saleData = await salesAPI.getById(id);

        console.log("Sale data received:", saleData);

        // Transform the API response to ensure correct data structure
        const formattedSale: Sale = {
          id: saleData.id,
          sale_date: saleData.sale_date || saleData.saleDate || "",
          buyer_name: saleData.buyer_name || saleData.buyerName || "",
          buyer_phone: saleData.buyer_phone || saleData.buyerPhone || "",
          total_price: Number(saleData.total_price || saleData.totalPrice) || 0,
          client_id: saleData.client_id || saleData.clientId || "",
          items: Array.isArray(saleData.items)
            ? saleData.items.map((item: any) => ({
                book_id: item.book_id || item.bookId,
                book_name: item.book_name || item.bookName,
                quantity: Number(item.quantity) || 1,
                price_per_book:
                  Number(item.price_per_book || item.pricePerBook) || 0,
                subtotal:
                  Number(item.subtotal) ||
                  Number(item.price_per_book || item.pricePerBook) *
                    (Number(item.quantity) || 1),
              }))
            : [
                // If items array is not present, create a legacy-compatible single item
                {
                  book_id: saleData.book_id || "",
                  book_name: saleData.book_name || "",
                  quantity: Number(saleData.quantity) || 1,
                  price_per_book: Number(saleData.price_per_book) || 0,
                  subtotal: Number(saleData.total_price) || 0,
                },
              ],
          created_at: saleData.created_at,
          updated_at: saleData.updated_at,
        };

        setSale(formattedSale);
        setLoading(false);
      } catch (error: any) {
        console.error("Error fetching sale:", error);
        setError(`Failed to fetch sale: ${error.message || "Unknown error"}`);
        setLoading(false);
      }
    };

    fetchSale();
  }, [id]);

  const calculateTotalPrice = (items: SaleItem[]): number => {
    return items.reduce((total, item) => total + item.subtotal, 0);
  };

  const handleBasicInfoChange = (field: string, value: string | number) => {
    setSale((prevSale) => {
      if (!prevSale) return null;

      return { ...prevSale, [field]: value };
    });
  };

  const handleItemQuantityChange = (bookId: string, quantity: number) => {
    if (!sale) return;

    const updatedItems = sale.items.map((item) => {
      if (item.book_id === bookId) {
        const newQuantity = Math.max(1, quantity);

        return {
          ...item,
          quantity: newQuantity,
          subtotal: newQuantity * item.price_per_book,
        };
      }

      return item;
    });

    const newTotalPrice = calculateTotalPrice(updatedItems);

    setSale({
      ...sale,
      items: updatedItems,
      total_price: newTotalPrice,
    });
  };

  const handleRemoveItem = (bookId: string) => {
    if (!sale) return;

    const updatedItems = sale.items.filter((item) => item.book_id !== bookId);
    const newTotalPrice = calculateTotalPrice(updatedItems);

    setSale({
      ...sale,
      items: updatedItems,
      total_price: newTotalPrice,
    });
  };

  const handleBookChange = (bookId: string) => {
    setSelectedBook(bookId);
    const book = books.find((b) => b.id === bookId);

    setSelectedBookDisplay(book ? book.name : "");
  };

  const handleAddBook = () => {
    if (!selectedBook || !sale) return;

    const book = books.find((b) => b.id === selectedBook);

    if (!book) return;

    // Check if book already exists in items
    const existingItemIndex = sale.items.findIndex(
      (item) => item.book_id === selectedBook,
    );

    if (existingItemIndex >= 0) {
      // Update existing item
      const updatedItems = [...sale.items];
      const existingItem = updatedItems[existingItemIndex];
      const newQuantity = existingItem.quantity + newItemQuantity;

      updatedItems[existingItemIndex] = {
        ...existingItem,
        quantity: newQuantity,
        subtotal: newQuantity * existingItem.price_per_book,
      };

      const newTotalPrice = calculateTotalPrice(updatedItems);

      setSale({
        ...sale,
        items: updatedItems,
        total_price: newTotalPrice,
      });
    } else {
      // Add new item
      const newItem: SaleItem = {
        book_id: book.id,
        book_name: book.name,
        quantity: newItemQuantity,
        price_per_book: book.price_per_book,
        subtotal: newItemQuantity * book.price_per_book,
      };

      const updatedItems = [...sale.items, newItem];
      const newTotalPrice = calculateTotalPrice(updatedItems);

      setSale({
        ...sale,
        items: updatedItems,
        total_price: newTotalPrice,
      });
    }

    // Reset selection
    setSelectedBook(null);
    setSelectedBookDisplay("");
    setNewItemQuantity(1);
  };

  const handleSave = async () => {
    if (sale && id) {
      try {
        await salesAPI.update(id, sale);
        alert(t("Sale updated successfully!"));
        navigate(`/sale/View/${id}`);
      } catch (error: any) {
        console.error("Error updating sale:", error);
        alert(
          t("Failed to update sale: ") + (error.message || "Unknown error"),
        );
      }
    }
  };

  // Convert date to YYYY-MM-DD format for HTML date input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return "";
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      return date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
    } catch (e) {
      console.error("Error formatting date for input:", e);
      return "";
    }
  };

  if (loading) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <p className="text-center">{t("Loading...")}</p>
        </div>
      </DefaultLayout>
    );
  }

  if (error) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <div className="bg-red-500 text-white p-4 rounded mb-4">{error}</div>
          <Link className="text-blue-500 text-center mt-4 block" to="/sales">
            {t("Back to Sales")}
          </Link>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="container mx-auto p-4">
        <div className="row py-4">
          <h1 className="text-3xl text-center py-4 font-bold mb-4">
            {t("Edit Sale")}
          </h1>
        </div>
        <div className="row" dir={isRtl ? "rtl" : "ltr"}>
          {sale ? (
            <div className="max-w-4xl mx-auto">
              {/* Basic Sale Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Input
                    label={t("Buyer Name")}
                    value={sale.buyer_name}
                    onChange={(e) =>
                      handleBasicInfoChange("buyer_name", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Input
                    label={t("Buyer Phone")}
                    value={sale.buyer_phone || ""}
                    onChange={(e) =>
                      handleBasicInfoChange("buyer_phone", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Input
                    label={t("Sale Date")}
                    type="date"
                    value={formatDateForInput(sale.sale_date)}
                    onChange={(e) =>
                      handleBasicInfoChange("sale_date", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Sale Items Table */}
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-4">{t("Items")}</h3>
                <Table aria-label="Sale items">
                  <TableHeader>
                    <TableColumn>{t("Book")}</TableColumn>
                    <TableColumn>{t("Quantity")}</TableColumn>
                    <TableColumn>{t("Price Per Book")}</TableColumn>
                    <TableColumn>{t("Subtotal")}</TableColumn>
                    <TableColumn>{t("Actions")}</TableColumn>
                  </TableHeader>
                  <TableBody emptyContent={t("No items in this sale")}>
                    {sale.items.map((item, index) => (
                      <TableRow key={`${item.book_id}-${index}`}>
                        <TableCell>{item.book_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              isIconOnly
                              className="min-w-6 w-6 h-6"
                              color="default"
                              size="sm"
                              onClick={() =>
                                handleItemQuantityChange(
                                  item.book_id,
                                  item.quantity - 1,
                                )
                              }
                            >
                              -
                            </Button>
                            <Input
                              className="w-14 min-w-0"
                              size="sm"
                              type="number"
                              value={item.quantity.toString()}
                              onChange={(e) => {
                                const newQty =
                                  parseInt(e.target.value, 10) || 1;

                                handleItemQuantityChange(item.book_id, newQty);
                              }}
                            />
                            <Button
                              isIconOnly
                              className="min-w-6 w-6 h-6"
                              color="default"
                              size="sm"
                              onClick={() =>
                                handleItemQuantityChange(
                                  item.book_id,
                                  item.quantity + 1,
                                )
                              }
                            >
                              +
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>{item.price_per_book}</TableCell>
                        <TableCell>{item.subtotal}</TableCell>
                        <TableCell>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => handleRemoveItem(item.book_id)}
                          >
                            {t("Remove")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Add New Book */}
              <div className="mb-6 p-4 bg-gray-50 rounded-md">
                <h3 className="text-lg font-medium mb-4">{t("Add Book")}</h3>
                <div className="flex flex-col md:flex-row items-end gap-4">
                  <div className="flex-1">
                    <label
                      className="block text-gray-700 mb-1 font-semibold"
                      htmlFor="book"
                    >
                      {t("Book")}
                    </label>
                    <Dropdown>
                      <DropdownTrigger>
                        <Button
                          className="w-full justify-between"
                          endContent={<span>▼</span>}
                          variant="flat"
                        >
                          {selectedBookDisplay || t("Select Book")}
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        aria-label="Book Selection"
                        className="max-h-80 overflow-y-auto"
                      >
                        {books.map((book) => (
                          <DropdownItem
                            key={book.id}
                            className="text-black"
                            onClick={() => handleBookChange(book.id)}
                          >
                            {book.name}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>

                  <div className="w-36">
                    <label
                      className="block text-gray-700 mb-1 font-semibold"
                      htmlFor="quantity"
                    >
                      {t("Quantity")}
                    </label>
                    <div className="flex items-center gap-1">
                      <Button
                        isIconOnly
                        className="min-w-8 h-8"
                        color="default"
                        disabled={!selectedBook}
                        size="sm"
                        onClick={() =>
                          setNewItemQuantity(Math.max(1, newItemQuantity - 1))
                        }
                      >
                        -
                      </Button>
                      <Input
                        className="flex-1"
                        disabled={!selectedBook}
                        id="quantity"
                        type="number"
                        value={newItemQuantity.toString()}
                        onChange={(e) =>
                          setNewItemQuantity(
                            Math.max(1, parseInt(e.target.value, 10) || 1),
                          )
                        }
                      />
                      <Button
                        isIconOnly
                        className="min-w-8 h-8"
                        color="default"
                        disabled={!selectedBook}
                        size="sm"
                        onClick={() => setNewItemQuantity(newItemQuantity + 1)}
                      >
                        +
                      </Button>
                    </div>
                  </div>

                  <Button
                    color="primary"
                    disabled={!selectedBook}
                    onClick={handleAddBook}
                  >
                    {t("Add to Sale")}
                  </Button>
                </div>
              </div>

              {/* Total Price */}
              <div className="mb-4">
                <div className="border-2 border-gray-300 dark:border-gray-700 p-4 rounded-md bg-gray-100 dark:bg-gray-800">
                  <div className="font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    {t("Total Price")}:
                  </div>
                  <div className="text-center text-2xl font-bold">
                    {typeof sale.total_price === "number"
                      ? `${sale.total_price.toFixed(2)} Rs`
                      : `${Number(sale.total_price).toFixed(2)} Rs`}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-4">
                <Button color="primary" onClick={handleSave}>
                  {t("Save")}
                </Button>
                <Button
                  color="danger"
                  onClick={() => navigate(`/sale/View/${id}`)}
                >
                  {t("Cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <p>{t("No sale found")}</p>
          )}

          <Spacer y={1} />
          <Link className="text-blue-500 text-center mt-4 block" to="/sales">
            {t("Back to Sales")}
          </Link>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default SaleEditPage;
